import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMoleculeFacts, type MoleculeFacts, type PublishedSource } from '@peruselab/api-client/molecules';
import { Icon } from '../../../components/Icon';
import { pubChemUrl } from './factsheetText';

/** While a first fetch runs: ask again this often, this many times, then say it's unavailable. */
const POLL_MS = 2000;
const POLL_TRIES = 15;

/**
 * ADR 0017 D6 (#906): a Molecule's published facts. The server never waits on PubChem, so a first
 * visit may answer "pending"; this asks again until the facts land, without reloading the page.
 * Null until the first answer.
 */
export function useMoleculeFacts(moleculeId: string): MoleculeFacts | null {
  const [facts, setFacts] = useState<MoleculeFacts | null>(null);

  useEffect(() => {
    let cancelled = false;
    const wait = () => new Promise((resolve) => setTimeout(resolve, POLL_MS));
    const load = async () => {
      for (let tries = 1; !cancelled; tries += 1) {
        const next = await getMoleculeFacts(moleculeId);
        if (cancelled) return;
        if (next.status !== 'pending') return setFacts(next);
        if (tries >= POLL_TRIES) return setFacts({ ...next, status: 'unavailable' });
        setFacts(next);
        await wait();
      }
    };
    setFacts(null);
    load().catch(() => {
      if (!cancelled) setFacts(null);
    });
    return () => {
      cancelled = true;
    };
  }, [moleculeId]);

  return facts;
}

export function OtherSources({ sources }: { readonly sources: readonly PublishedSource[] }) {
  const { t } = useTranslation();
  if (sources.length === 0) return null;
  return (
    <p className="published-other-sources">
      <span>{t('moleculePage.otherSources')}</span>{' '}
      {sources.map((source, index) => (
        <span key={source.name}>
          {index > 0 ? ', ' : ''}
          {source.url ? (
            <a href={source.url} target="_blank" rel="noopener noreferrer">
              {source.name}
            </a>
          ) : (
            source.name
          )}
        </span>
      ))}
      <span className="published-other-sources-why"> {t('moleculePage.otherSourcesWhy')}</span>
    </p>
  );
}

/** ADR 0017 (#906): PubChem's own pages for this compound -- the full record, its literature and patents. */
export function LearnMore({ cid }: { readonly cid: number }) {
  const { t } = useTranslation();
  return (
    <section className="published-facts-section" aria-labelledby="molecule-learn-more">
      <h2 id="molecule-learn-more">{t('moleculePage.learnMore')}</h2>
      <ul className="published-learn-more">
        {(
          [
            [undefined, 'learnMorePubChem'],
            ['Literature', 'learnMoreLiterature'],
            ['Patents', 'learnMorePatents']
          ] as const
        ).map(([section, key]) => (
          <li key={key}>
            <a href={pubChemUrl(cid, section)} target="_blank" rel="noopener noreferrer" className="button-with-icon">
              <span>{t(`moleculePage.${key}`)}</span>
              <Icon name="openInNew" size="0.9rem" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
